import { Vec2 } from './math';
import type { TextSpec } from './text';
import type { DimensionEntity } from './types';

export interface DimensionAxes {
  /** Direction of the measurement (unit vector). */
  dir: Vec2;
  /** Left normal of `dir`; `offset` is measured along it. */
  normal: Vec2;
  /** Dimension line endpoints (projected onto the offset line). */
  d1: Vec2;
  d2: Vec2;
  /** Measured value in millimetres. */
  value: number;
}

export interface DimensionGeometry {
  /** Dimension line + extension lines. */
  lines: Vec2[][];
  /** Filled arrowhead triangles. */
  arrows: Vec2[][];
  text: TextSpec;
}

const MIN_LENGTH = 1e-9;

function projectOntoAxis(point: Vec2, linePoint: Vec2, normal: Vec2): Vec2 {
  return point.add(normal.scale(linePoint.sub(point).dot(normal)));
}

/** Resolves the measurement frame of a dimension, or `null` when degenerate. */
export function dimensionAxes(entity: DimensionEntity): DimensionAxes | null {
  const base = entity.p2.sub(entity.p1);
  if (base.len() < MIN_LENGTH) return null;

  const dir =
    entity.kind === 'aligned'
      ? base.normalize()
      : Math.abs(base.x) >= Math.abs(base.y)
        ? Vec2.of(1, 0)
        : Vec2.of(0, 1);
  const normal = dir.perp();
  const anchor = entity.p1.add(normal.scale(entity.offset));
  return {
    dir,
    normal,
    d1: projectOntoAxis(entity.p1, anchor, normal),
    d2: projectOntoAxis(entity.p2, anchor, normal),
    value: Math.abs(base.dot(dir)),
  };
}

export function dimensionValue(entity: DimensionEntity): number {
  return dimensionAxes(entity)?.value ?? 0;
}

/** Measurement text, honouring overrides, prefixes and precision. */
export function dimensionLabel(entity: DimensionEntity): string {
  const override = entity.textOverride?.trim();
  if (override) return override;
  const decimals = Math.max(0, Math.min(entity.precision, 6));
  return `${entity.prefix ?? ''}${dimensionValue(entity).toFixed(decimals)}${entity.suffix ?? ''}`;
}

export function dimensionGeometry(entity: DimensionEntity): DimensionGeometry {
  const height = Math.max(entity.textHeight, 1e-6);
  const arrow = Math.max(entity.arrowSize, 1e-6);
  const axes = dimensionAxes(entity);

  if (!axes) {
    return {
      lines: [],
      arrows: [],
      text: {
        text: dimensionLabel(entity),
        position: entity.p1,
        height,
        rotation: 0,
        anchor: 'center',
        valign: 'bottom',
        widthFactor: 1,
      },
    };
  }

  const { dir, normal, d1, d2 } = axes;
  const sign = entity.offset >= 0 ? 1 : -1;
  const gap = height * 0.5;
  const overshoot = arrow * 0.8;

  const lines: Vec2[][] = [
    [d1, d2],
    [entity.p1.add(normal.scale(sign * gap)), d1.add(normal.scale(sign * overshoot))],
    [entity.p2.add(normal.scale(sign * gap)), d2.add(normal.scale(sign * overshoot))],
  ];

  const arrows: Vec2[][] = [];
  const addArrow = (tip: Vec2, inward: Vec2): void => {
    const back = tip.add(inward.scale(arrow));
    const side = inward.perp().scale(arrow * 0.32);
    arrows.push([tip, back.add(side), back.sub(side)]);
  };
  addArrow(d1, dir);
  addArrow(d2, dir.neg());

  const mid = Vec2.mid(d1, d2);
  let rotation = dir.angle();
  let textNormal = normal.scale(sign);
  if (rotation > Math.PI / 2 + 1e-9 || rotation < -Math.PI / 2 - 1e-9) {
    rotation += Math.PI;
    textNormal = textNormal.neg();
  }

  return {
    lines,
    arrows,
    text: {
      text: dimensionLabel(entity),
      position: mid.add(textNormal.scale(height * 0.58)),
      height,
      rotation,
      anchor: 'center',
      valign: 'baseline',
      widthFactor: 1,
    },
  };
}
